import { useMemo } from "react";
import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../services/query";
import { FieldEnrollmentsDataEnvelope } from "../../../models/enrollments";

import { useFieldEnrollmentsQuery } from "./fieldEnrollments.query";

export * from "./fieldEnrollments.mutations";
export * from "./fieldEnrollments.invalidate";

export const fieldEnrollmentsAccessError = new Error(
  "Attempted to access field enrollments data but it is not available",
);

// ###############################
// ## useFieldEnrollmentsStatus ##
// ###############################

export type ResultOfUseFieldEnrollmentsStatus = QueryStatus;
export type UseFieldEnrollmentsStatus = () => ResultOfUseFieldEnrollmentsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the field enrollments data.
 */
export const useFieldEnrollmentsStatus: UseFieldEnrollmentsStatus = () => {
  return useFieldEnrollmentsQuery({ watch: ["status"] }).status;
};

// ##############################
// ## useFieldEnrollmentsError ##
// ##############################

export type ResultOfUseFieldEnrollmentsError = AxiosError | null;
export type UseFieldEnrollmentsError = () => ResultOfUseFieldEnrollmentsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the field enrollments data
 * or null if no error occurred.
 */
export const useFieldEnrollmentsError: UseFieldEnrollmentsError = () => {
  return useFieldEnrollmentsQuery({ watch: ["error"] }).error;
};

// ##################################
// ## useMaybeFieldEnrollmentsData ##
// ##################################

export type ResultOfUseMaybeFieldEnrollmentsData =
  | FieldEnrollmentsDataEnvelope
  | undefined;
export type UseMaybeFieldEnrollmentsData =
  () => ResultOfUseMaybeFieldEnrollmentsData;

/**
 * The returned value of this hook will contain
 * the field enrollments data, which may be undefined
 */
export const useMaybeFieldEnrollmentsData: UseMaybeFieldEnrollmentsData =
  () => {
    return useFieldEnrollmentsQuery({ watch: ["data"] }).data;
  };

// #############################
// ## useFieldEnrollmentsData ##
// #############################

export type ResultOfUseFieldEnrollmentsData = FieldEnrollmentsDataEnvelope;
export type UseFieldEnrollmentsData = () => ResultOfUseFieldEnrollmentsData;

/**
 * The returned value of this hook will contain the field enrollments data.
 *
 * This hook is guaranteed to contain field enrollments data or will throw.
 */
export const useFieldEnrollmentsData: UseFieldEnrollmentsData = () => {
  const data = useMaybeFieldEnrollmentsData();
  if (data === undefined) {
    throw fieldEnrollmentsAccessError;
  }
  return data;
};

// ##############################
// ## useMaybeFieldEnrollments ##
// ##############################

export type ParamsOfUseMaybeFieldEnrollments = { forceDisable?: boolean };
export type ResultOfUseMaybeFieldEnrollments =
  QueryEnvelope<FieldEnrollmentsDataEnvelope>;
export type UseMaybeFieldEnrollments = (
  params?: ParamsOfUseMaybeFieldEnrollments,
) => ResultOfUseMaybeFieldEnrollments;

/**
 * The `data` property returned from this hook will contain
 * the field enrollments data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the field enrollments data.
 */
export const useMaybeFieldEnrollments: UseMaybeFieldEnrollments = ({
  forceDisable,
} = {}) => {
  const { status, error, data } = useFieldEnrollmentsQuery({
    watch: ["status", "error", "data"],
    forceDisable,
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// #########################
// ## useFieldEnrollments ##
// #########################

export type ResultOfUseFieldEnrollments =
  EnsuredQueryEnvelope<FieldEnrollmentsDataEnvelope>;
export type UseFieldEnrollments = () => ResultOfUseFieldEnrollments;

/**
 * The `data` property returned from this hook will contain
 * the field enrollments data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the field enrollments data.
 *
 * This hook is guaranteed to contain field enrollments data or will throw.
 */
export const useFieldEnrollments: UseFieldEnrollments = () => {
  const { status, error, data } = useFieldEnrollmentsQuery({
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw fieldEnrollmentsAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
